const entryCreate = require('./contentful/entry-create');
const EntryService = require('../services/EntryService').default;
const entryUpdate = require('./contentful/entry-update');
const block = require('./block');
const getSpace = require('./contentful/get-space');
const transformer = require('./transformers/article');
const linkResolve = require('./contentful/link-resolve');
const processError = require('./contentful/error-process');
const update = require('immutability-helper');
const generationModel = require('./generation');

let entryService = null;

const gettingEntryService = () => {
  if (!entryService) {
    entryService = getSpace().then((space) => {
      return new EntryService(space, transformer);
    });
  }

  return entryService;
};

const createArticle = (space, article, generationId, depth) => {
  return block.saveBlockArray([], article.bodytext, true).then((bodyText) => {
    const newArticle = update(article, {
      bodytext: {
        $set: bodyText
      }
    });

    const data = transformer.internalToContentful(newArticle);
    return entryCreate(space, 'articlecollection', data);
  }).then((entry) => {
    const data = entry.toPlainObject();
    return linkResolve(space, data, depth);
  }).then((data) => {
    const transformedArticle = transformer.contentfulToInternal(data);
    if (generationId) {
      Promise.all([
        generationModel.getById(generationId, 2)
      ]).then(([generation]) => {
        generation.articles.push(transformedArticle);
        generationModel.save(generation);
      });
    }
    return transformedArticle;
  });
};


const updateArticle = (space, article, depth) => {
  return space.getEntry(article.id).catch(
    processError
  ).then((entry) => {
    const data = entry.toPlainObject();
    let currentBodytextLinks = [];

    if (data.fields.bodytext && data.fields.bodytext['nb-NO']) {
      currentBodytextLinks = data.fields.bodytext['nb-NO'];
    }

    return block.saveBlockArray(currentBodytextLinks, article.bodytext, false).then((newBodyText) => {
      const newArticle = update(article, {
        bodytext: {
          $set: newBodyText
        }
      });

      const data = transformer.internalToContentful(newArticle);
      return entryUpdate(entry, data);
    }).then((entry) => {
      const data = entry.toPlainObject();
      return linkResolve(space, data, depth);
    }).then((data) => {
      return transformer.contentfulToInternal(data);
    });
  });
};

module.exports = {
  all(depth = 2) {
    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'articlecollection', // eslint-disable-line
        'order': '-sys.updatedAt',
        'sys.archivedAt[exists]': false,
        'limit': 999
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return linkResolve(space, data, depth).then((entries) => {
          return entries.items.map(transformer.contentfulToInternal);
        });
      });
    });
  },

  allByParams(depth = 0, offset = 0, limit = 100, params = {}, order) {
    return getSpace().then((space) => {
      const entryParams = {
        content_type: 'articlecollection', // eslint-disable-line
        order: order || '-sys.updatedAt',
        skip: offset,
        limit: limit
      };

      if (!params.includeArchived) {
        entryParams['sys.archivedAt[exists]'] = false;
      }

      if (params.makeId) {
        entryParams['fields.makes.sys.id'] = params.makeId;
      }

      if (params.motorforum) {
        entryParams['fields.motorforum'] = params.motorforum;
      } else {
        entryParams['fields.motorforum[ne]'] = true;
      }

      return space.getEntries(entryParams).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return linkResolve(space, data, depth).then((entries) => {
          return [entries.items.map(transformer.contentfulToInternal), entries.total];
        });
      });
    });
  },

  createEmpty(props = {}) {
    return getSpace().then((space) => {
      return space.getContentType('articlecollection').then((contentType) => {
        let pagesOptions = [];
        if (contentType && contentType.fields) {
          // populate options
          const options = contentType.fields.find((field) => {
            return field.id === 'pages';
          });
          if (options) {
            pagesOptions = options.items.validations[0].in.filter((option) => {
              // Use "Motorforum *" options for motorforum articles, and others for non mf articles
              if (props.motorforum) {
                return option.startsWith('Motorforum ');
              }

              return !option.startsWith('Motorforum ');
            });
          }
        }

        return {
          id: 'temp',
          title: 'Ny artikkel',
          caption: '',
          bodytext: [],
          priority: 0,
          listImage: null,
          mainImage: null,
          summary: null,
          makes: [],
          models: [],
          pages: [],
          pagesOptions: pagesOptions,
          fromDate: null,
          toDate: null,
          dealers: [],
          metadata: {},
          ctaForm: null,
          ctaFormExt: null,
          ctaTitle: null,
          ctaText: null,
          ctaButtonText: null,
          categories: [],
          generationLink: {},
          motorforum: props.motorforum
        };
      });
    });
  },

  save(article, generationId, depth) {
    return getSpace().then((space) => {
      if (article.id === 'temp') {
        return createArticle(space, article, generationId, depth);
      }
      return updateArticle(space, article, depth);
    });
  },

  getById(...parameters) {
    return gettingEntryService().then((service) => service.getById(...parameters));
  },

  publish(...parameters) {
    return gettingEntryService().then((service) => service.publish(...parameters));
  },

  publishWithBlocks(article, depth) {
    const bodytextIdsToPublish = article.bodytext.reduce((ids, md) => {
      if (!md.published || md.updated) {
        ids.push(md.id);
      }

      return ids;
    }, []);

    if (!bodytextIdsToPublish.length) {
      return gettingEntryService().then((service) => service.publish(article.id, depth));
    }

    return block.publishBlocks(bodytextIdsToPublish).then(() => {
      return gettingEntryService().then((service) => service.publish(article.id, depth));
    });
  },

  unpublish(...parameters) {
    return gettingEntryService().then((service) => service.unpublish(...parameters));
  },

  archive(...parameters) {
    return gettingEntryService().then((service) => service.archive(...parameters));
  },

  unarchive(...parameters) {
    return gettingEntryService().then((service) => service.unarchive(...parameters));
  },

  query(select, filters = {}) {
    return getSpace().then((space) => {
      return space.getEntries({
        content_type: 'articlecollection',
        select,
        ...filters
      }).catch(
        processError
      );
    });
  },

  search(config) {
    config = Object.assign({
      query: '',
      limit: 10,
      offset: 0,
      contentType: 'articlecollection',
      options: {}
    }, config || {});
    return getSpace().then((space) => {
      const options = Object.assign({}, config.options, {
        'query': config.query,
        'skip': config.offset,
        'limit': config.limit,
        'sys.archivedAt[exists]': false,
        'order': '-sys.updatedAt'
      });

      options.content_type = 'articlecollection';

      if (config.filter) {
        options['fields.makes.sys.id'] = config.filter;
      }

      return space.getEntries(
        options
      ).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return linkResolve(space, data, 2).then((entries) => {
          return {
            'items': entries.items.map(transformer.contentfulToInternal),
            'limit': entries.limit,
            'offset': entries.skip,
            'total': entries.total,
            'sys.archivedAt[exists]': false
          };
        });
      });
    });
  }
};
